import { supabase } from "@/integrations/supabase/client";
import type { Database, Json } from "@/integrations/supabase/types";
import type { LlmModelId } from "@smartmed/llm-models";
import { safeJsonParse } from "@/lib/jsonParse";
import type { CombineEndoscopyHistologyResult, RunStageResult, RunStageResultGpSummary } from "@/types/classification";
import type { QueryData, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

const safeParseJsonRunResult = <T extends RunStageResult | RunStageResultGpSummary>(result: string): T | null => {
  const parsedResult = safeJsonParse(result);

  if (!parsedResult) {
    console.error("Failed to parse run result", result);
    return null;
  }

  return parsedResult as T;
};

const transformTestRunStageResult = <
  TInput extends { result: Json },
  TResult extends RunStageResult | RunStageResultGpSummary | CombineEndoscopyHistologyResult,
>(
  stageRow: TInput | null,
) => {
  if (!stageRow) {
    return null;
  }

  const parsedResult = safeParseJsonRunResult<TResult>(stageRow.result as string);
  if (!parsedResult) {
    return null;
  }

  return {
    ...stageRow,
    result: parsedResult,
  } as Omit<TInput, "result"> & { result: TResult };
};

function getRunsQuery() {
  return supabase.from("test_classification_run_results").select(`
    id,
    run_start_time,
    run_end_time, success,
    llm_model_id
  `);
}

function getTestEndoscopyResultsQuery() {
  return supabase.from("test_endoscopy_results").select(
    `*,
    testData: medical_text_examples(
      test_type,
      sequence_number,
      label
    )
  `,
  );
}
type EndoscopyData = QueryData<ReturnType<typeof getTestEndoscopyResultsQuery>>[number];

function getTestHistologyResultsQuery() {
  return supabase.from("test_histology_results").select(
    `*,
              testData: medical_text_examples(
                test_type,
                sequence_number,
                label
              )
              `,
  );
}
type HistologyData = QueryData<ReturnType<typeof getTestHistologyResultsQuery>>[number];

function getTestGpSummaryResultsQuery() {
  return supabase.from("test_gp_summary_results").select(
    `*,
              testData: medical_text_examples(
                test_type,
                sequence_number,
                label
              )
    `,
  );
}
type GpSummaryData = QueryData<ReturnType<typeof getTestGpSummaryResultsQuery>>[number];

function getTestEncounterQuery() {
  return supabase.from("test_encounter_results").select(
    `
      *,
      patient_encounters(
        id,
        admit_datetime,
        patient: patients(
          id
        )
      )
    `,
  );
}
type EncounterData = QueryData<ReturnType<typeof getTestEncounterQuery>>[number];

function getTestCombineEndoHistoQuery() {
  return supabase.from("test_combine_endo_histo_results").select(
    `
      *
    `,
  );
}
type CombineEndoHistoData = QueryData<ReturnType<typeof getTestCombineEndoHistoQuery>>[number];

export type TestRun = Omit<QueryData<ReturnType<typeof getRunsQuery>>[number], "llm_model_id"> & {
  llm_model_id: LlmModelId | null;
  encounter: EncounterData | null;
  endoscopyResult:
    | (Omit<EndoscopyData, "result"> & {
        result: RunStageResult;
      })
    | null;
  gpSummaryResult:
    | (Omit<GpSummaryData, "result"> & {
        result: RunStageResultGpSummary;
      })
    | null;
  histologyResult:
    | (Omit<HistologyData, "result"> & {
        result: RunStageResult;
      })
    | null;
  combineEndoHistoResult:
    | (Omit<CombineEndoHistoData, "result"> & {
        result: CombineEndoscopyHistologyResult;
      })
    | null;
};

export interface TestRunsFilter {
  filterStartTime?: Date | null;
}

export const useTestRunsWithRealtime = (filter?: TestRunsFilter) => {
  const [runs, setRuns] = useState<TestRun[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch initial data (and refetch on filter change)
  useEffect(() => {
    setIsLoading(true);

    let query = getRunsQuery().order("run_start_time", { ascending: false });
    if (filter?.filterStartTime) {
      // Format as 'YYYY-MM-DD HH:mm:ss.SSS' in utc time
      const filterStartTime = dayjs(filter.filterStartTime).utc().format("YYYY-MM-DD HH:mm:ss.SSS");
      query = query.gte("run_start_time", filterStartTime);
    } else {
      // When no time filter is applied, limit to recent runs to prevent performance issues
      query = query.limit(100);
    }

    query.then(async ({ data, error }) => {
      if (error) {
        setError(error);
        setIsLoading(false);
      } else {
        // fetch the associated encounter, test_endoscopy_result, test_gp_summary_result, test_histology_result
        // Use Promise.allSettled to handle individual query failures gracefully
        const [
          testEncounterResults,
          testEndoscopyResults,
          testGpSummaryResults,
          testHistologyResults,
          testCombineResults,
        ] = await Promise.allSettled([
          getTestEncounterQuery().in(
            "test_classification_run_results_id",
            data.map((run) => run.id),
          ),
          getTestEndoscopyResultsQuery().in(
            "test_classification_run_results_id",
            data.map((run) => run.id),
          ),
          getTestGpSummaryResultsQuery().in(
            "test_classification_run_results_id",
            data.map((run) => run.id),
          ),
          getTestHistologyResultsQuery().in(
            "test_classification_run_results_id",
            data.map((run) => run.id),
          ),
          getTestCombineEndoHistoQuery().in(
            "test_classification_run_results_id",
            data.map((run) => run.id),
          ),
        ]);

        // Extract data from settled promises, handling failures gracefully
        const testEncounterData: {
          data: EncounterData[] | null;
          error: Error | null;
        } =
          testEncounterResults.status === "fulfilled"
            ? { data: testEncounterResults.value.data, error: null }
            : { data: null, error: testEncounterResults.reason };
        const testEndoscopyData: {
          data: EndoscopyData[] | null;
          error: Error | null;
        } =
          testEndoscopyResults.status === "fulfilled"
            ? { data: testEndoscopyResults.value.data, error: null }
            : { data: null, error: testEndoscopyResults.reason };
        const testGpSummaryData: {
          data: GpSummaryData[] | null;
          error: Error | null;
        } =
          testGpSummaryResults.status === "fulfilled"
            ? { data: testGpSummaryResults.value.data, error: null }
            : { data: null, error: testGpSummaryResults.reason };
        const testHistologyData: {
          data: HistologyData[] | null;
          error: Error | null;
        } =
          testHistologyResults.status === "fulfilled"
            ? { data: testHistologyResults.value.data, error: null }
            : { data: null, error: testHistologyResults.reason };
        const testCombineData: {
          data: CombineEndoHistoData[] | null;
          error: Error | null;
        } =
          testCombineResults.status === "fulfilled"
            ? { data: testCombineResults.value.data, error: null }
            : { data: null, error: testCombineResults.reason };

        // Log any query failures for debugging
        if (testEncounterData.error) console.error("Test encounter query failed:", testEncounterData.error);
        if (testEndoscopyData.error) console.error("Test endoscopy query failed:", testEndoscopyData.error);
        if (testGpSummaryData.error) console.error("Test GP summary query failed:", testGpSummaryData.error);
        if (testHistologyData.error) console.error("Test histology query failed:", testHistologyData.error);
        if (testCombineData.error) console.error("Test combine query failed:", testCombineData.error);

        // now attach the related endo/histo/gp to the relevant run
        const runsWithResults = data.map((run) => {
          const encounterResult = testEncounterData.data?.find(
            (result) => result.test_classification_run_results_id === run.id,
          );
          const endoscopyResult = testEndoscopyData.data?.find(
            (result) => result.test_classification_run_results_id === run.id,
          );
          const gpSummaryResult = testGpSummaryData.data?.find(
            (result) => result.test_classification_run_results_id === run.id,
          );
          const histologyResult = testHistologyData.data?.find(
            (result) => result.test_classification_run_results_id === run.id,
          );
          const combineResult = testCombineData.data?.find(
            (result) => result.test_classification_run_results_id === run.id,
          );

          return {
            ...run,
            llm_model_id: run.llm_model_id as LlmModelId | null,
            encounter: encounterResult || null,
            endoscopyResult: transformTestRunStageResult<EndoscopyData, RunStageResult>(endoscopyResult ?? null),
            gpSummaryResult: transformTestRunStageResult<GpSummaryData, RunStageResultGpSummary>(
              gpSummaryResult ?? null,
            ),
            histologyResult: transformTestRunStageResult<HistologyData, RunStageResult>(histologyResult ?? null),
            combineEndoHistoResult: transformTestRunStageResult<CombineEndoHistoData, CombineEndoscopyHistologyResult>(
              combineResult ?? null,
            ),
          };
        });

        setRuns(runsWithResults);
        setIsLoading(false);
      }
    });
    return () => {};
  }, [filter?.filterStartTime]);

  // Subscribe to realtime changes
  useEffect(() => {
    const channel = supabase
      .channel("test_classification_run_results-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "test_classification_run_results",
        },
        async (
          payload: RealtimePostgresChangesPayload<
            Database["public"]["Tables"]["test_classification_run_results"]["Row"]
          >,
        ) => {
          const { eventType, new: newRow, old: oldRow } = payload;

          if (!newRow || !("id" in newRow) || !newRow.id) {
            return;
          }

          const [
            testEncounterResult,
            testEndoscopyResult,
            testGpSummaryResult,
            testHistologyResult,
            testCombineResult,
          ] = await Promise.all([
            getTestEncounterQuery().eq("test_classification_run_results_id", newRow.id).maybeSingle(),
            getTestEndoscopyResultsQuery().eq("test_classification_run_results_id", newRow.id).maybeSingle(),
            getTestGpSummaryResultsQuery().eq("test_classification_run_results_id", newRow.id).maybeSingle(),
            getTestHistologyResultsQuery().eq("test_classification_run_results_id", newRow.id).maybeSingle(),
            getTestCombineEndoHistoQuery().eq("test_classification_run_results_id", newRow.id).maybeSingle(),
          ]);

          // Ensure enrichedRunData is always a TestRun
          const enrichedRunData: TestRun = {
            ...newRow,
            llm_model_id: newRow.llm_model_id as LlmModelId | null,
            encounter: testEncounterResult.data || null,
            endoscopyResult: transformTestRunStageResult<EndoscopyData, RunStageResult>(testEndoscopyResult.data),
            gpSummaryResult: transformTestRunStageResult<GpSummaryData, RunStageResultGpSummary>(
              testGpSummaryResult.data,
            ),
            histologyResult: transformTestRunStageResult<HistologyData, RunStageResult>(testHistologyResult.data),
            combineEndoHistoResult: transformTestRunStageResult<CombineEndoHistoData, CombineEndoscopyHistologyResult>(
              testCombineResult.data,
            ),
          };

          setRuns((prevRuns) => {
            if (!prevRuns) return prevRuns;

            if (eventType === "INSERT") {
              // Add new run to the top
              return enrichedRunData ? [enrichedRunData, ...prevRuns] : prevRuns;
            } else if (eventType === "UPDATE") {
              // Replace the updated run
              return enrichedRunData
                ? prevRuns.map((run) =>
                    run.id === enrichedRunData.id ? ({ ...run, ...enrichedRunData } as TestRun) : run,
                  )
                : prevRuns;
            } else if (eventType === "DELETE") {
              // Remove the deleted run
              return oldRow && "id" in oldRow && oldRow.id ? prevRuns.filter((run) => run.id !== oldRow.id) : prevRuns;
            }
            return prevRuns;
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { runs, isLoading, error };
};
